import React from "react";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    FileDoneOutlined,
    ManOutlined,
    WomanOutlined,
} from "@ant-design/icons";
import { getJalaliDateTime } from "@/lib/utils/date";
import RenderIf from "@/components/render-if";
import Barcode from "react-barcode";
import pStyles from  './Print.module.css'

/* ---------- وضعیت‌ها ---------- */
const STUDENT_STATUS = {
    DELIVERED: "delivered",
    PRINTED: "printed",
    PENDING: "pending",
    DROPPED: "dropped",
    NO_ITEM: "no_item",
};

const STATUS_LABEL = {
    [STUDENT_STATUS.DELIVERED]: "تحویل گرفته",
    [STUDENT_STATUS.PRINTED]: "چاپ شده",
    [STUDENT_STATUS.PENDING]: "در انتظار تحویل",
    [STUDENT_STATUS.DROPPED]: "انصراف",
    [STUDENT_STATUS.NO_ITEM]: "بدون قلم",
};

/* ---------- استایل پرینتر حرارتی ---------- */
const styles = {
    page: {
        width: "80mm",
        minHeight: "100mm",
        padding: "4mm 3mm",
        margin: "0 auto",
        background: "#fff",
        color: "#000",
        // fontFamily: "Tahoma, 'IRANSans', sans-serif",
        fontSize: "11px",
        lineHeight: 1.7,
        direction: "rtl",
        textAlign: "right",
        boxSizing: "border-box",
    },
    header: {
        textAlign: "center",
        borderBottom: "1px dashed #000",
        paddingBottom: "3mm",
        marginBottom: "3mm",
    },
    title: {
        fontSize: "14px",
        fontWeight: "bold",
        margin: "0 0 1mm 0",
    },
    subTitle: {
        fontSize: "10px",
        margin: 0,
        color: "#333",
    },
    sectionTitle: {
        fontSize: "12px",
        fontWeight: "bold",
        margin: "3mm 0 1mm 0",
        paddingBottom: "1mm",
        borderBottom: "1px solid #000",
    },
    classBox: {
        border: "1px solid #000",
        borderRadius: "2mm",
        padding: "2mm",
        marginBottom: "3mm",
        pageBreakInside: "avoid",
    },
    classHeader: {
        fontSize: "12px",
        fontWeight: "bold",
        marginBottom: "2mm",
        paddingBottom: "1mm",
        borderBottom: "1px dashed #000",
    },
    line: {
        borderBottom: "1px dashed #000",
    },
    row: {
        display: "flex",
        justifyContent: "space-between",
        fontSize: "10px",
        margin: "0.5mm 0",
    },
    statsGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: "1mm",
        margin: "1.5mm 0",
    },
    statCell: {
        border: "1px solid #000",
        borderRadius: "1mm",
        padding: "1mm",
        textAlign: "center",
    },
    statLabel: {
        fontSize: "8px",
        display: "block",
        color: "#333",
    },
    statValue: {
        fontSize: "12px",
        fontWeight: "bold",
        display: "block",
    },
    productTag: {
        fontSize: "9px",
        border: "1px solid #000",
        borderRadius: "1mm",
        padding: "0.5mm 1.5mm",
        margin: "0.5mm 0.5mm 0 0",
        display: "inline-block",
    },
    studentRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontSize: "10px",
        padding: "1mm 0",
        borderBottom: "1px dotted #999",
        gap: "1mm",
    },
    studentInfo: {
        flex: 1,
        minWidth: 0,
    },
    studentName: {
        fontWeight: "bold",
        display: "block",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    studentMeta: {
        fontSize: "8px",
        color: "#333",
        display: "block",
    },
    statusTag: {
        fontSize: "8px",
        border: "1px solid #000",
        borderRadius: "1mm",
        padding: "0.3mm 1mm",
        whiteSpace: "nowrap",
    },
    historyLine: {
        fontSize: "8px",
        color: "#333",
        marginTop: "0.5mm",
    },
    footer: {
        marginTop: "4mm",
        paddingTop: "2mm",
        borderTop: "1px dashed #000",
        textAlign: "center",
        fontSize: "9px",
    },
    divider: {
        borderTop: "1px dashed #000",
        margin: "2mm 0",
    },
};

/* ---------- کامپوننت ---------- */
function ProductReceiptPrintV3({ classes = [], payload = {}, keyword = "" }) {
    const now = getJalaliDateTime(new Date());


    if (!classes || classes.length === 0) {
        return null;
    }

    return (
        <>

            {/* ---------- کلاس‌ها ---------- */}
            {classes.map((cls) => (
                <RenderIf key={cls.id} condition={cls?.stats?.stepStatus !== 'delivered'}>
                    <div className={pStyles.receiptPage}>
                        <ClassBlock key={cls.lessonId} cls={cls} />
                    </div>
                </RenderIf>
            ))}

        </>
    );
}

/* ---------- بلوک هر کلاس ---------- */
function ClassBlock({ cls }) {
    const { lesson, step, rows, stats } = cls;

    return (
        <div style={styles.classBox}>
            <div className={'flex justify-center flex-col items-center'}>
                <div className={''}>
                    {document.title}
                </div>

                <div className={''}>
                    رسید تحویل اقلام آموزشی
                </div>
            </div>
            <div className={styles.line}><hr/></div>

            {/* هدر کلاس */}
            <div style={styles.classHeader}>
                کلاس: {lesson?.code ?? "-"}
                <br/>
                مربی: {stats.teacher?.firstname} {stats.teacher?.lastname}
                {stats.teacher?.mobile && ` (${stats.teacher.mobile})`}
            </div>


            {stats.stepCode && (
                <div style={styles.row}>
                    <div className={'text-center flex flex-col items-center gap-1 justify-center'}>
                        <Barcode
                            value={stats.stepCode}
                            format="CODE128"
                            height={45}
                            width={1.6}
                            margin={0}
                            displayValue={false}
                        />
                        <div className={' flex gap-2'}>
                            <div>کد تحویل</div>
                            <div className={'font-bold'}>{stats.stepCode}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* آمار کلاس */}
            <div style={styles.statsGrid} className={'flex justify-center'}>
                {/*<div style={styles.statCell}>*/}
                {/*    <span style={styles.statLabel}>کل</span>*/}
                {/*    <span style={styles.statValue}>{stats.total}</span>*/}
                {/*</div>*/}
                {/*<div style={styles.statCell}>*/}
                {/*    <span style={styles.statLabel}>در لیست</span>*/}
                {/*    <span style={styles.statValue}>{stats.inClassList}</span>*/}
                {/*</div>*/}
                {/*<div style={styles.statCell}>*/}
                {/*    <span style={styles.statLabel}>تحویل گرفته</span>*/}
                {/*    <span style={styles.statValue}>{stats.delivered}</span>*/}
                {/*</div>*/}
                {/*<div style={styles.statCell}>*/}
                {/*    <span style={styles.statLabel}>چاپ</span>*/}
                {/*    <span style={styles.statValue}>{stats.printed}</span>*/}
                {/*</div>*/}
                {/*<div style={styles.statCell}>*/}
                {/*    <span style={styles.statLabel}>قابل تحویل</span>*/}
                {/*    <span style={styles.statValue}>{stats.pending}</span>*/}
                {/*</div>*/}
                {/*<div style={styles.statCell}>*/}
                {/*    <span style={styles.statLabel}>انصراف</span>*/}
                {/*    <span style={styles.statValue}>{stats.dropped}</span>*/}
                {/*</div>*/}

                <div style={styles.statCell}>
                    <span style={styles.statLabel}>دختر</span>
                    <span style={styles.statValue}>{stats.female}</span>
                </div>
                <div style={styles.statCell}>
                    <span style={styles.statLabel}>پسر</span>
                    <span style={styles.statValue}>{stats.male}</span>
                </div>
            </div>

            {/* تفکیک محصول */}
            {false && Object.keys(stats.byProduct).length > 0 && (
                <>
                    <div style={{...styles.sectionTitle, fontSize: "10px", marginTop: "2mm"}}>
                        تفکیک محصولات
                    </div>
                    <div>
                        {Object.entries(stats.byProduct).map(([title, s]) => (
                            <div key={title} className={'font-bold text-center'}>
                                <strong>{title}</strong>
                                <div className={'text-xs'}>
                                    قابل تحویل
                                    {s.pending}
                                </div>

                            </div>
                        ))}
                    </div>

                </>
            )}
            <div style={styles.line}></div>
            {/* لیست دانش‌آموزان */}
            <div style={{...styles.sectionTitle, fontSize: "10px", marginTop: "2mm"}}>
                لیست دانش‌آموزان ({rows.length} نفر)
            </div>
            <div>
                {rows.map((row) => (
                    <StudentRow key={row.key} row={row}/>
                ))}
            </div>
        </div>
    );
}

/* ---------- ردیف دانش‌آموز ---------- */
function StudentRow({row}) {
    const d = row.detail || {};


    return (
        <div style={styles.studentRow}>
            <div style={styles.studentInfo}>
                <span className={'w-full flex justify-between font-bold'}>
                    <div>
                        {d?.firstname} {d?.lastname}
                    </div>
                    <div>
                        {d?.gender === "female" && " (دختر)"}
                        {d?.gender === "male" && " (پسر)"}
                    </div>

                </span>
                <span style={styles.studentMeta}>

                </span>

            </div>
            <div>
                {row?.status ==='no_item' && <div style={{ ...styles.statusTag, marginTop: "0.5mm", display: "block" }}>بدون اقلام</div>}
                {row?.status ==='delivered' && <div style={{ ...styles.statusTag, marginTop: "0.5mm", display: "block" }}>تحویل گرفته</div>}
                {!row.inClassList && (
                    <div style={{ ...styles.statusTag, marginTop: "0.5mm", display: "block" }}>
                        انصراف
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProductReceiptPrintV3;